import React, { createElement, MouseEvent, useCallback } from 'react';
import { BarDatum, BarItemProps } from '@nivo/bar';
import { useTooltip } from '@nivo/tooltip';

const POINT_RADIUS_PX = 5;
const POINT_STROKE_WIDTH_PX = 2;

/**
 * Reemplazo de la barra de nivo (`barComponent`) que dibuja un punto en el valor de cada
 * barra, para usar `ResponsiveBar` como gráfica de puntos sin agregar dependencias. Es un
 * `rect` con esquinas redondeadas al radio (se ve como círculo) para conservar el tipo de
 * evento que esperan los handlers de nivo.
 */
const ChartPoint = <RawDatum extends BarDatum>({
  bar,
  isInteractive,
  tooltip,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isFocusable,
  ariaLabel,
}: BarItemProps<RawDatum>) => {
  const { showTooltipFromEvent, showTooltipAt, hideTooltip } = useTooltip();
  const centerX = bar.x + bar.width / 2;
  const diameter = POINT_RADIUS_PX * 2;

  const renderTooltip = useCallback(
    () => createElement(tooltip, { ...bar, ...bar.data }),
    [tooltip, bar],
  );

  const handleMouseEnter = useCallback((event: MouseEvent<SVGRectElement>) => {
    onMouseEnter?.(bar.data, event);
    showTooltipFromEvent(renderTooltip(), event);
  }, [bar.data, onMouseEnter, showTooltipFromEvent, renderTooltip]);

  const handleMouseMove = useCallback((event: MouseEvent<SVGRectElement>) => {
    showTooltipFromEvent(renderTooltip(), event);
  }, [showTooltipFromEvent, renderTooltip]);

  const handleMouseLeave = useCallback((event: MouseEvent<SVGRectElement>) => {
    onMouseLeave?.(bar.data, event);
    hideTooltip();
  }, [bar.data, onMouseLeave, hideTooltip]);

  const handleClick = useCallback((event: MouseEvent<SVGRectElement>) => {
    onClick?.({ color: bar.color, ...bar.data }, event);
  }, [bar.color, bar.data, onClick]);

  const handleFocus = useCallback(() => {
    showTooltipAt(renderTooltip(), [bar.absX + bar.width / 2, bar.absY]);
  }, [showTooltipAt, renderTooltip, bar.absX, bar.absY, bar.width]);

  return (
    <rect
      data-testid="chart-point"
      x={centerX - POINT_RADIUS_PX}
      y={bar.y - POINT_RADIUS_PX}
      width={diameter}
      height={diameter}
      rx={POINT_RADIUS_PX}
      ry={POINT_RADIUS_PX}
      fill={bar.color}
      stroke="white"
      strokeWidth={POINT_STROKE_WIDTH_PX}
      tabIndex={isFocusable ? 0 : undefined}
      aria-label={ariaLabel ? ariaLabel(bar.data) : undefined}
      onMouseEnter={isInteractive ? handleMouseEnter : undefined}
      onMouseMove={isInteractive ? handleMouseMove : undefined}
      onMouseLeave={isInteractive ? handleMouseLeave : undefined}
      onClick={isInteractive ? handleClick : undefined}
      onFocus={isInteractive && isFocusable ? handleFocus : undefined}
      onBlur={isInteractive && isFocusable ? hideTooltip : undefined}
    />
  );
};

export default ChartPoint;
